import * as yup from 'yup';
import {
  PROFILE_FIELD_TYPES,
  validateProfileFieldValueClient,
  serializeProfileFieldValueForApi,
  deserializeProfileFieldValueForForm,
} from './constants';

export const OPTIONS_REQUIRED_MSG =
  'Add at least one answer choice for single-select and multi-select fields.';

export const fieldNeedsSelectOptions = (fieldType) =>
  fieldType === PROFILE_FIELD_TYPES.SINGLE_SELECT ||
  fieldType === PROFILE_FIELD_TYPES.MULTI_SELECT;

/**
 * One empty row when switching to single/multi select (global library modals).
 * @param {boolean} forEdit - include option_id for edit form payloads
 */
export function ensureProfileFieldOptionsForSelectType(
  fieldType,
  options,
  forEdit = false,
) {
  if (!fieldNeedsSelectOptions(fieldType)) {
    return [];
  }
  if (options?.length) {
    return options;
  }
  return forEdit ? [{ name: '', option_id: null }] : [{ name: '' }];
}

/** Shared modal panel styles for Manage Profile Fields add/edit */
export const globalProfileFieldModalPaperSx = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  borderRadius: '8px',
  boxShadow: 24,
  p: 4,
  width: '100%',
  minWidth: { xs: 280, sm: 480 },
  maxWidth: {
  xs: '30vw', // mobile
  sm: '35vw', // tablet
  md: '40vw', // small laptop
  lg: '45vw', // desktop
  xl: '50vw', // large screens
},
  maxHeight: 'calc(100vh - 80px)',
  overflow: 'auto',
  '&::-webkit-scrollbar': { width: '8px' },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: '#44444461',
    borderRadius: '8px',
  },
  '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#666' },
};

/** Formik/Yup options array error for global profile field forms */
export function getOptionsErrorMessage(optionsError) {
  if (!optionsError) {
    return null;
  }
  if (typeof optionsError === 'string') {
    return optionsError;
  }
  if (Array.isArray(optionsError)) {
    return null;
  }
  return OPTIONS_REQUIRED_MSG;
}

/** Yup rule for options on Manage Profile Fields add/edit */
export function globalProfileFieldOptionsYup() {
  return yup.array().when('field_type', {
    is: (fieldType) => fieldNeedsSelectOptions(fieldType),
    then: (schema) =>
      schema
        .min(1, OPTIONS_REQUIRED_MSG)
        .of(
          yup.object({
            name: yup
              .string()
              .trim()
              .required('Each choice must have a value.'),
          }),
        ),
    otherwise: (schema) => schema.optional().default([]),
  });
}

/** Formik touched value for a select field's options array */
export function buildSelectOptionsTouched(needsOpts, options, optionKey = 'name') {
  if (!needsOpts) {
    return false;
  }
  if (options?.length) {
    return options.map(() => ({ [optionKey]: true }));
  }
  return true;
}

/** Touch state so option validation shows after Save (global library form) */
export function buildGlobalFieldOptionsTouched(values) {
  const needsOpts = fieldNeedsSelectOptions(values?.field_type);
  return {
    options: buildSelectOptionsTouched(needsOpts, values?.options, 'name'),
  };
}

/** Validation errors for all member Profile Data tabs */
export function validateMemberProfileFields(
  allTabGroups,
  cartUserIds,
  formValues,
) {
  const errors = {};
  (allTabGroups || []).forEach((tabFieldGroups) => {
    Object.assign(
      errors,
      validateMemberProfileTabFields(tabFieldGroups, cartUserIds, formValues),
    );
  });
  return errors;
}

/** Validation errors for one member Profile Data tab */
export function validateMemberProfileTabFields(
  tabFieldGroups,
  cartUserIds,
  formValues,
) {
  const errors = {};
  if (!tabFieldGroups?.length) {
    return errors;
  }
  tabFieldGroups.forEach((group) => {
    const fields = group.profile_fields || [];
    cartUserIds.forEach((userId) => {
      fields.forEach((field) => {
        const key = memberProfileFieldKey(userId, field);
        const displayValue = deserializeProfileFieldValueForForm(
          field,
          formValues[key],
        );
        const msg = validateProfileFieldValueClient(field, displayValue);
        if (msg) {
          errors[key] = msg;
        }
      });
    });
  });
  return errors;
}

/** Filter and map profile field groups for checkout save payload */
export function mapCheckoutUserFieldGroups(
  userId,
  profileFieldsList,
  cartItems,
  packageProductData,
  isItemMatching,
  mapFieldData,
) {
  return profileFieldsList
    .map((group) =>
      mapCheckoutGroupFieldsForUser(
        group,
        userId,
        cartItems,
        packageProductData,
        isItemMatching,
        mapFieldData,
      ),
    )
    .filter((groupFields) => groupFields?.length > 0);
}

function profileFieldMatchesCartItem(
  userId,
  profileField,
  cartItems,
  packageProductData,
  isItemMatching,
) {
  return cartItems?.some((item) =>
    isItemMatching(item, userId, profileField, packageProductData),
  );
}

function mapCheckoutGroupFieldsForUser(
  group,
  userId,
  cartItems,
  packageProductData,
  isItemMatching,
  mapFieldData,
) {
  const profileFields = group?.profile_fields;
  if (!profileFields) {
    return [];
  }
  return profileFields
    .filter((profileField) =>
      profileFieldMatchesCartItem(
        userId,
        profileField,
        cartItems,
        packageProductData,
        isItemMatching,
      ),
    )
    .map((profileField) => mapFieldData(profileField, userId));
}

/** Build validation errors keyed by profile_field_id */
export function validateProductProfileFields(profileFields, formValues) {
  const errors = {};
  profileFields?.forEach((group) =>
    group?.profile_fields?.forEach((field) => {
      const fieldId = field.profile_field_id;
      const displayValue = deserializeProfileFieldValueForForm(
        field,
        formValues[fieldId],
      );
      const msg = validateProfileFieldValueClient(field, displayValue);
      if (msg) {
        errors[fieldId] = msg;
      }
    }),
  );
  return errors;
}

/** Build fieldById map for serialization */
export function buildProfileFieldByIdMap(profileFields) {
  const fieldById = {};
  profileFields?.forEach((group) =>
    group?.profile_fields?.forEach((field) => {
      fieldById[field.profile_field_id] = field;
    }),
  );
  return fieldById;
}

/** Serialize formValues for POST /user-profiles-data */
export function serializeProductProfileFieldsPayload(
  formValues,
  profileFields,
  productId,
  profileId,
) {
  const fieldById = buildProfileFieldByIdMap(profileFields);
  return Object.entries(formValues).map(([key, value]) => {
    const field = fieldById[key];
    const displayValue = deserializeProfileFieldValueForForm(field, value);
    return {
      profile_field_id: Number(key),
      value: serializeProfileFieldValueForApi(field, displayValue),
      product_id: productId,
      profile_id: profileId,
    };
  });
}

/** Form key used on member account Profile Data page */
export function memberProfileFieldKey(userId, field) {
  return `${userId}-${field.profile_field_name}-${field.profile_id}`;
}

/** Stable key for useEffect deps — avoids refetch when parent passes a new array reference. */
export function getProfileFieldsStructureKey(profileFields) {
  if (!profileFields?.length) {
    return '';
  }
  return profileFields
    .map((group) =>
      (group?.profile_fields || [])
        .map((field) => field.profile_field_id ?? '')
        .join(','),
    )
    .join('|');
}

/** Profile fields in cart that apply to a given user (product or package line items). */
export function filterProfileFieldsForCartUser(
  profileFields,
  userId,
  cartData,
  packageProductData,
  isItemMatching,
) {
  const fields = [];
  profileFields?.forEach((group) =>
    group?.profile_fields?.forEach((field) => {
      const matches = cartData?.some((item) =>
        isItemMatching(item, userId, field, packageProductData),
      );
      if (matches) {
        fields.push(field);
      }
    }),
  );
  return fields;
}

/** True when a required field still needs a valid value (empty or invalid option). */
export function isRequiredProfileFieldIncomplete(field, storedValue) {
  const isRequired =
    field?.is_checked === 1 || field?.is_checked === true;
  if (!isRequired) {
    return false;
  }
  const displayValue = deserializeProfileFieldValueForForm(field, storedValue);
  return Boolean(validateProfileFieldValueClient(field, displayValue));
}

/**
 * Rows from GET /profile-fields/product/:productId/:userId — keep only
 * required fields that are missing or have invalid/deleted option values.
 */
export function collectIncompleteRequiredProfileFields(apiRows, profileFields) {
  const fieldById = buildProfileFieldByIdMap(profileFields);
  const seen = new Set();
  const incomplete = [];

  for (const row of apiRows || []) {
    const key = `${row.profile_field_id}_${row.profile_id}_${row.user_id}`;
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);

    const fieldDef = fieldById[row.profile_field_id] || {
      field_type: row.field_type,
      options: row.options || [],
      is_checked: row.is_checked ?? 1,
      profile_field_name: row.profile_field_name,
    };

    if (isRequiredProfileFieldIncomplete(fieldDef, row.value)) {
      incomplete.push({ ...row, user_id: row.user_id });
    }
  }

  return incomplete;
}

/** Validation errors keyed by memberProfileFieldKey (checkout cart popup). */
export function validateCheckoutProfileFields(
  cartUserIds,
  profileFields,
  formValues,
  cartData,
  packageProductData,
  isItemMatching,
) {
  const errors = {};
  cartUserIds?.forEach((userId) => {
    filterProfileFieldsForCartUser(
      profileFields,
      userId,
      cartData,
      packageProductData,
      isItemMatching,
    ).forEach((field) => {
      const formKey = memberProfileFieldKey(userId, field);
      const displayValue = deserializeProfileFieldValueForForm(
        field,
        formValues[formKey],
      );
      const msg = validateProfileFieldValueClient(field, displayValue);
      if (msg) {
        errors[formKey] = msg;
      }
    });
  });
  return errors;
}

/** Prefill checkout profile popup from GET /user-profiles-data/:userId responses. */
export function mapCheckoutProfileDataToFormValues(allData, profileFields) {
  const fieldById = buildProfileFieldByIdMap(profileFields);
  return (allData || []).reduce((acc, row) => {
    const key = memberProfileFieldKey(row.user_id, {
      profile_field_name: row.profile_field_name,
      profile_id: row.profile_id,
    });
    const fieldDef = fieldById[row.profile_field_id] || {
      field_type: row.field_type,
      options: row.options || [],
      is_checked: row.is_checked,
      profile_field_name: row.profile_field_name,
    };
    acc[key] = deserializeProfileFieldValueForForm(fieldDef, row.value);
    return acc;
  }, {});
}

/** Prefill member Profile Data form from GET /user-profiles-data/:userId */
export function mapMemberProfileDataToFormValues(allData, profileFields) {
  const fieldById = buildProfileFieldByIdMap(profileFields);
  return (allData || []).reduce((acc, row) => {
    const fieldDef = fieldById[row.profile_field_id] || {
      field_type: row.field_type,
      options: row.options || [],
      is_checked: row.is_checked,
      profile_field_name: row.profile_field_name,
    };
    const key = memberProfileFieldKey(row.user_id, {
      profile_field_name: row.profile_field_name,
      profile_id: row.profile_id,
    });
    acc[key] = deserializeProfileFieldValueForForm(fieldDef, row.value);
    return acc;
  }, {});
}

/** Prefill form from GET /user-profiles-data/:userId */
export function mapUserProfileDataToFormValues(allData, profileFields) {
  const fieldById = buildProfileFieldByIdMap(profileFields);
  return (allData || []).reduce((acc, row) => {
    const fieldId = row.profile_field_id;
    const fieldDef = fieldById[fieldId] || {
      field_type: row.field_type,
      options: row.options || [],
      is_checked: row.is_checked,
      profile_field_name: row.profile_field_name,
    };
    acc[fieldId] = deserializeProfileFieldValueForForm(fieldDef, row.value);
    return acc;
  }, {});
}
